// Enhanced PRNG with multiple entropy sources and improved mixing
export class SeededRandom {
    constructor(seed) {
        // Initialize multiple seeds for better entropy
        this.seed = this.hash(seed);
        this.seed2 = this.hash(this.seed + 1);
        this.seed3 = this.hash(this.seed2 + 2);
        this.seed4 = this.hash(this.seed3 + 3);
        this.initialSeed = this.seed;
    }

    // 32-bit hash function for better seed distribution
    hash(x) {
        x = Number(x);
        x = ((x >> 16) ^ x) * 0x45d9f3b;
        x = ((x >> 16) ^ x) * 0x45d9f3b;
        x = (x >> 16) ^ x;
        return x >>> 0;
    }

    // Enhanced random number generation with multiple mixing functions
    random() {
        // Update all seeds using different mixing functions
        this.seed = (this.seed * 1597 + 51749) % 244944;
        this.seed2 = (this.seed2 * 1597 + 51749) % 244944;
        this.seed3 = (this.seed3 * 1597 + 51749) % 244944;
        this.seed4 = (this.seed4 * 1597 + 51749) % 244944;

        // Combine seeds using XOR and rotation
        let result = 0;
        
        // Mix first seed using Mulberry32
        let t = this.seed += 0x6D2B79F5;
        t = Math.imul(t ^ t >>> 15, t | 1);
        t ^= t + Math.imul(t ^ t >>> 7, t | 61);
        result ^= ((t ^ t >>> 14) >>> 0);

        // Mix second seed using PCG
        let word = ((this.seed2 >>> 0) * 747796405 + 2891336453) >>> 0;
        word = ((word >>> ((word >>> 28) + 4)) ^ word) * 277803737;
        result ^= (word ^ (word >>> 22)) >>> 0;

        // Mix third seed using xoshiro128**
        let t2 = this.seed3;
        let t3 = this.seed4;
        t2 ^= t2 << 23;
        t2 ^= t2 >> 17;
        t2 ^= t3;
        t2 ^= t3 >> 26;
        result ^= t2 >>> 0;

        // Final mixing step
        result = Math.imul(result, 747796405);
        result = Math.imul(result ^ (result >>> 15), 2891336453);
        result = Math.imul(result ^ (result >>> 13), 277803737);
        result ^= result >>> 16;

        // Convert to 0-1 range with high precision
        return (result >>> 0) / 4294967296;
    }

    // Reset all seeds to initial state
    reset() {
        this.seed = this.initialSeed;
        this.seed2 = this.hash(this.seed + 1);
        this.seed3 = this.hash(this.seed2 + 2);
        this.seed4 = this.hash(this.seed3 + 3);
    }

    // Get random number between min and max
    range(min, max) {
        return min + (max - min) * this.random();
    }

    // Get random integer between min and max (inclusive)
    rangeInt(min, max) {
        return Math.floor(this.range(min, max + 1));
    }

    // Get random boolean with given probability
    chance(probability) {
        return this.random() < probability;
    }

    // Get random item from array
    pick(array) {
        return array[Math.floor(this.random() * array.length)];
    }

    // Get seeded timestamp relative to base time
    getTimestamp(baseTime, maxOffset) {
        return baseTime + Math.floor(this.random() * maxOffset);
    }

    // Shuffle array using Fisher-Yates algorithm
    shuffle(array) {

        const result = [...array];
        for (let i = result.length - 1; i > 0; i--) {
            const j = Math.floor(this.random() * (i + 1));
            [result[i], result[j]] = [result[j], result[i]];
        }

        return result;
    }
}